/**
 * Google + Facebook sign-in helpers.
 *
 * We lazy-load each provider's SDK only when the user is on a page that
 * actually offers the button — keeps the initial bundle small and avoids
 * Google / Facebook telemetry firing on pages where the user is logged in
 * already.
 *
 * Backend flow:
 *   - Provider SDK gives us a token (Google: ID token JWT; Facebook: access token)
 *   - POST it to /auth/social/{provider}
 *   - Backend verifies + returns the same TokenPair shape as /auth/login
 *   - We hand it to auth.setSession() and treat it the same as a password login.
 */

import { api } from '../api.js';

// Provider IDs essentially never change in prod (rotating a Google OAuth
// client is rare and a deliberate ops event). Caching them in localStorage
// makes the second-and-later visits to /login or /signup not pay the
// /auth/config RTT at all, and the social buttons render as soon as the
// GSI script is ready. The background refresh keeps the cache fresh so a
// rotated id propagates on the next visit.
const CONFIG_CACHE_KEY = 'triviana.auth_config_v1';
const CONFIG_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

let configCache = null;
let inflightConfigFetch = null;

function _readCachedConfig() {
  try {
    const raw = localStorage.getItem(CONFIG_CACHE_KEY);
    if (!raw) return null;
    const { value, exp } = JSON.parse(raw);
    if (!value || typeof exp !== 'number') return null;
    if (Date.now() >= exp) return null;
    return value;
  } catch {
    return null;
  }
}

function _persistConfig(value) {
  try {
    localStorage.setItem(
      CONFIG_CACHE_KEY,
      JSON.stringify({ value, exp: Date.now() + CONFIG_CACHE_TTL_MS }),
    );
  } catch {
    // Quota / private mode — non-fatal.
  }
}

async function _fetchFreshConfig() {
  // Dedupe in-flight fetches across the two pages so a quick
  // signup -> login navigation never queues a second request.
  if (inflightConfigFetch) return inflightConfigFetch;
  inflightConfigFetch = (async () => {
    try {
      const fresh = await api('/auth/config');
      _persistConfig(fresh);
      return fresh;
    } catch {
      // Backend offline / network — fall back to "no providers" only if
      // we have nothing cached. Buttons simply won't render rather than
      // breaking the page.
      return { google_client_id: null, facebook_app_id: null };
    } finally {
      inflightConfigFetch = null;
    }
  })();
  return inflightConfigFetch;
}

/** Fetch (and cache) the public /auth/config — which provider IDs are live.
 *
 * Reads the localStorage cache first so repeat visits don't pay the RTT.
 * When the cache hits, a background refresh kicks off so a rotated client
 * id propagates to the next visit without ever blocking this one.
 */
export async function loadSocialAuthConfig() {
  if (configCache) return configCache;
  const cached = _readCachedConfig();
  if (cached) {
    configCache = cached;
    // Refresh in the background — non-blocking.
    void _fetchFreshConfig().then((fresh) => {
      configCache = fresh;
    });
    return configCache;
  }
  configCache = await _fetchFreshConfig();
  return configCache;
}

/** Eager warm-up of the Google + (cached) config network paths. Call this
 * at the very top of the auth pages so the GSI script download starts in
 * parallel with — not after — the /auth/config fetch. The actual
 * `renderGoogleButton` call awaits the same promise, so this is purely a
 * latency reduction with no side effects on render.
 *
 * Best-effort: a network error here is swallowed, the real call site
 * will see the same error and surface it normally.
 */
export function prefetchSocialScripts() {
  // Fire-and-forget. Both promises are memoised so the caller's later
  // `await` resolves immediately.
  loadGoogleScript().catch(() => {});
  // Don't preload the Facebook SDK — it's only needed on user click,
  // and FB's SDK is heavier (~200KB). Loading it speculatively would
  // hurt every visit to outweigh the one-tap-faster benefit.
}

// --- Google -----------------------------------------------------------------

let googleScriptPromise = null;

function loadGoogleScript() {
  if (googleScriptPromise) return googleScriptPromise;
  googleScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-google-gsi]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google));
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    // Force English regardless of the browser's Accept-Language. Without
    // `?hl=en` GIS renders in whatever locale the device is set to —
    // that surfaced as Arabic sign-in buttons for users with Arabic OS
    // language. Facebook is already pinned via `/en_US/` in its URL.
    s.src = 'https://accounts.google.com/gsi/client?hl=en';
    s.async = true;
    s.defer = true;
    s.dataset.googleGsi = '1';
    s.addEventListener('load', () => resolve(window.google));
    s.addEventListener('error', reject);
    document.head.appendChild(s);
  });
  return googleScriptPromise;
}

/**
 * Render a Google sign-in button into `container`. Calls `onCredential(idToken)`
 * after a successful credential response. Returns a `destroy()` function.
 */
export async function renderGoogleButton(container, { clientId, onCredential, label = 'continue_with' }) {
  if (!clientId) return () => {};
  await loadGoogleScript();
  if (!window.google?.accounts?.id) return () => {};

  window.google.accounts.id.initialize({
    client_id: clientId,
    callback: (response) => {
      if (response?.credential) onCredential(response.credential);
    },
    // Disable FedCM auto-select; we want the user to choose a Google account
    // each time on these pages (the auth/onboarding path).
    auto_select: false,
  });
  window.google.accounts.id.renderButton(container, {
    theme: 'filled_black',
    size: 'large',
    type: 'standard',
    text: label,
    shape: 'pill',
    logo_alignment: 'left',
    width: container.offsetWidth || 320,
  });
  return () => {
    try { window.google.accounts.id.cancel(); } catch { /* ignore */ }
    container.innerHTML = '';
  };
}

// --- Facebook ---------------------------------------------------------------

let fbScriptPromise = null;
let fbInitDone = false;

function loadFacebookScript(appId) {
  if (fbScriptPromise) return fbScriptPromise;
  fbScriptPromise = new Promise((resolve, reject) => {
    // FB recommends defining fbAsyncInit BEFORE the script loads.
    window.fbAsyncInit = function () {
      if (!fbInitDone) {
        window.FB.init({
          appId,
          cookie: false,
          xfbml: false,
          version: 'v19.0',
        });
        fbInitDone = true;
      }
      resolve(window.FB);
    };
    const existing = document.querySelector('script[data-fb-sdk]');
    if (existing) {
      // Script already loaded — fbAsyncInit may have already fired.
      if (fbInitDone) resolve(window.FB);
      return;
    }
    const s = document.createElement('script');
    s.src = 'https://connect.facebook.net/en_US/sdk.js';
    s.async = true;
    s.defer = true;
    s.crossOrigin = 'anonymous';
    s.dataset.fbSdk = '1';
    s.addEventListener('error', reject);
    document.head.appendChild(s);
  });
  return fbScriptPromise;
}

/**
 * Trigger a Facebook login popup. Resolves with the access token, or null
 * if the user cancelled. Throws on SDK / network failure.
 */
export async function signInWithFacebook({ appId }) {
  if (!appId) throw new Error('Facebook sign-in is not configured.');
  const FB = await loadFacebookScript(appId);
  return new Promise((resolve, reject) => {
    FB.login(
      (response) => {
        if (response?.status === 'connected') {
          resolve(response.authResponse?.accessToken || null);
        } else if (response?.status === 'unknown') {
          // User closed the popup.
          resolve(null);
        } else {
          reject(new Error('Facebook sign-in failed.'));
        }
      },
      { scope: 'email,public_profile' },
    );
  });
}

// --- Backend hand-off -------------------------------------------------------

/**
 * Exchange a provider token for Triviana auth tokens. Returns the TokenPair
 * (same shape as /auth/login) so callers can `auth.setSession(tokens)`.
 */
export async function exchangeSocialToken({ provider, token, referralCode = null }) {
  return api(`/auth/social/${provider}`, {
    method: 'POST',
    body: {
      token,
      // Server applies the code ONLY on fresh creation (returning
      // logins ignore it), so passing it through every time is safe.
      ...(referralCode ? { referral_code: referralCode } : {}),
    },
  });
}
